import { nextTick, onBeforeUnmount, onMounted, watch, type Ref } from 'vue'

interface UseLoadOlderOnScrollOptions {
  /** The scrollport whose top edge triggers loading. Also the observer root. */
  container: Ref<HTMLElement | null>
  /** A zero-height element at the very top of the list, above the messages. */
  sentinel: Ref<HTMLElement | null>
  /** Whether there are older messages left to load. */
  hasMore: () => boolean
  /** Whether an older-page load is already in flight. */
  isLoading: () => boolean
  /** Loads the next older page into the list (prepends). */
  loadOlder: () => Promise<void>
  /**
   * How early to start loading, expressed as an IntersectionObserver rootMargin.
   * The default extends the root 600px above the viewport so the next page
   * begins loading before the user reaches the top.
   */
  rootMargin?: string
}

/**
 * Auto-loads older messages as the user approaches the top of the timeline, and
 * keeps the reading position fixed while the prepended page lands.
 *
 * Anchor a visible message so simultaneous streaming at the bottom cannot move
 * the reading position. Fall back to bottom distance when no row is measurable.
 */
export function useLoadOlderOnScroll(options: UseLoadOlderOnScrollOptions) {
  const rootMargin = options.rootMargin ?? '600px 0px 0px 0px'
  let observer: IntersectionObserver | null = null
  // Guards against re-entrancy between the observer firing and the store's own
  // loadingOlder flag flipping (there's a tick where neither has caught up yet).
  let triggering = false

  async function trigger() {
    const container = options.container.value
    if (!container || triggering || options.isLoading() || !options.hasMore()) return

    triggering = true
    const viewportTop = container.getBoundingClientRect().top
    const anchor = [...container.querySelectorAll<HTMLElement>('[data-message-id]')]
      .find((node) => node.getBoundingClientRect().bottom > viewportTop)
    const anchorTop = anchor?.getBoundingClientRect().top
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight

    try {
      await options.loadOlder()
      await nextTick()
      const el = options.container.value
      if (el && anchor && anchorTop !== undefined && el.contains(anchor)) {
        el.scrollTop += anchor.getBoundingClientRect().top - anchorTop
      } else if (el) el.scrollTop = el.scrollHeight - el.clientHeight - distanceFromBottom
    } finally {
      triggering = false
    }
  }

  function connect() {
    disconnect()
    const root = options.container.value
    const target = options.sentinel.value
    if (!root || !target || typeof IntersectionObserver === 'undefined') return

    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) void trigger()
      },
      { root, rootMargin, threshold: 0 },
    )
    observer.observe(target)
  }

  function disconnect() {
    observer?.disconnect()
    observer = null
  }

  onMounted(connect)
  onBeforeUnmount(disconnect)
  // Re-attach if either element is (re)created after mount.
  watch([options.container, options.sentinel], connect)

  return { trigger }
}
